import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, Phone, ShoppingBag, ShoppingCart, CheckCircle, XCircle,
  Loader2, Clock, User, Package, ChevronDown, ChevronUp, Eye,
  DollarSign, Hash, AlertCircle, X, RefreshCw, Store, TrendingUp,
  BarChart3, Settings, Percent, ArrowRight, Shield, Trash2, Save,
  Users, Calendar, Image, Lock, Unlock
} from 'lucide-react';
import { publicStudioAPI } from '../../api';
import toast from 'react-hot-toast';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';

// ==================== Analytics Card ====================
const AnalyticsCard = ({ icon: Icon, label, value, subtext, color = 'blue', trend }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    purple: 'bg-blue-100 text-blue-600',
    amber: 'bg-amber-100 text-amber-600',
    rose: 'bg-rose-100 text-rose-600'
  };
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-center gap-3">
        <div className={`p-2.5 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          <p className="text-xs text-slate-500">{label}</p>
          {subtext && <p className="text-xs text-slate-400 mt-0.5">{subtext}</p>}
        </div>
        {trend !== undefined && (
          <div className={`text-xs font-medium px-2 py-1 rounded-full ${
            trend > 0 ? 'bg-emerald-100 text-emerald-700' : 
            trend < 0 ? 'bg-red-100 text-red-700' : 
            'bg-slate-100 text-slate-600'
          }`}>
            {trend > 0 ? '+' : ''}{trend}%
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== Analytics Panel ====================
const AnalyticsPanel = ({ period, onPeriodChange }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      try {
        const response = await publicStudioAPI.getAnalytics({ period });
        setAnalytics(response.data);
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
        toast.error('Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [period]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12 bg-slate-50 rounded-xl">
        <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500">No analytics data available</p>
      </div>
    );
  }

  const { summary, conversions, top_tried_skus, charts } = analytics;

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {['1d', '7d', '30d'].map(p => (
            <button
              key={p}
              onClick={() => onPeriodChange(p)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                period === p 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {p === '1d' ? 'Today' : p === '7d' ? '7 Days' : '30 Days'}
            </button>
          ))}
        </div>
        <p className="text-sm text-slate-500">
          {new Date(analytics.period_start).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })} - {new Date(analytics.period_end).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <AnalyticsCard
          icon={Store}
          label="Total Sessions"
          value={summary.total_sessions}
          subtext={`${summary.active_sessions} active now`}
          color="blue"
        />
        <AnalyticsCard
          icon={Eye}
          label="Total Try-ons"
          value={summary.total_tryons}
          subtext={`${summary.total_items_tried} unique items`}
          color="blue"
        />
        <AnalyticsCard
          icon={ShoppingCart}
          label="Checkouts"
          value={summary.completed_checkouts}
          subtext={summary.total_sessions > 0 ? `${Math.round((summary.completed_checkouts / summary.total_sessions) * 100)}% rate` : null}
          color="emerald"
        />
        <AnalyticsCard
          icon={DollarSign}
          label="Revenue"
          value={`₹${summary.total_revenue.toLocaleString('en-IN')}`}
          subtext={summary.completed_checkouts > 0 ? `Avg: ₹${Math.round(summary.avg_order_value).toLocaleString('en-IN')}` : null}
          color="amber"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Sessions Chart */}
        {charts?.daily_sessions?.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-600" />
              Sessions Over Time
            </h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={charts.daily_sessions}>
                  <defs>
                    <linearGradient id="sessionsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 11, fill: '#64748b' }} 
                    tickFormatter={(val) => new Date(val).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
                  <Tooltip 
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                    labelFormatter={(val) => new Date(val).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#8b5cf6" 
                    strokeWidth={2}
                    fill="url(#sessionsGradient)" 
                    name="Sessions"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Daily Revenue Chart */}
        {charts?.daily_revenue?.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-emerald-600" />
              Revenue Over Time
            </h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={charts.daily_revenue}>
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 11, fill: '#64748b' }} 
                    tickFormatter={(val) => new Date(val).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  />
                  <YAxis 
                    tick={{ fontSize: 11, fill: '#64748b' }} 
                    tickFormatter={(val) => `₹${val >= 1000 ? `${(val/1000).toFixed(0)}k` : val}`}
                  />
                  <Tooltip 
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                    labelFormatter={(val) => new Date(val).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                    formatter={(val) => [`₹${val.toLocaleString('en-IN')}`, 'Revenue']}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="amount" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    fill="url(#revenueGradient)" 
                    name="Revenue"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Conversion Funnel */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-blue-600" />
          Conversion Funnel
        </h3>
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-2">
          {/* Sessions with Try-ons */}
          <div className="text-center flex-1 w-full sm:w-auto py-4 bg-blue-50 rounded-lg">
            <p className="text-3xl font-bold text-blue-700">{conversions.sessions_with_tryons}</p>
            <p className="text-xs text-blue-600 mt-1">Sessions with Try-ons</p>
          </div>
          
          {/* Arrow & Rate */}
          <div className="flex sm:flex-col items-center gap-1 sm:px-4">
            <ArrowRight className="w-5 h-5 text-slate-300 hidden sm:block" />
            <div className="w-8 h-0.5 bg-slate-200 sm:hidden" />
            <p className="text-sm font-semibold text-blue-600">{conversions.tryon_to_cart_rate}%</p>
          </div>
          
          {/* Sessions Added to Cart */}
          <div className="text-center flex-1 w-full sm:w-auto py-4 bg-blue-50 rounded-lg">
            <p className="text-3xl font-bold text-blue-700">{conversions.sessions_with_cart}</p>
            <p className="text-xs text-blue-600 mt-1">Added to Cart</p>
          </div>
          
          {/* Arrow & Rate */}
          <div className="flex sm:flex-col items-center gap-1 sm:px-4">
            <ArrowRight className="w-5 h-5 text-slate-300 hidden sm:block" />
            <div className="w-8 h-0.5 bg-slate-200 sm:hidden" />
            <p className="text-sm font-semibold text-emerald-600">{conversions.cart_to_purchase_rate}%</p>
          </div>
          
          {/* Purchased */}
          <div className="text-center flex-1 w-full sm:w-auto py-4 bg-emerald-50 rounded-lg">
            <p className="text-3xl font-bold text-emerald-700">{summary.completed_checkouts}</p>
            <p className="text-xs text-emerald-600 mt-1">Purchased</p>
          </div>
        </div>
      </div>

      {/* Top Tried SKUs */}
      {top_tried_skus?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-blue-600" />
            Most Tried Products
          </h3>
          <div className="space-y-2">
            {top_tried_skus.slice(0, 5).map((sku, idx) => (
              <div key={idx} className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
                <div className="flex items-center gap-3">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    idx === 0 ? 'bg-amber-100 text-amber-700' :
                    idx === 1 ? 'bg-slate-200 text-slate-700' :
                    idx === 2 ? 'bg-orange-100 text-orange-700' :
                    'bg-slate-100 text-slate-500'
                  }`}>
                    {idx + 1}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-slate-800">{sku.product_name}</p>
                    <p className="text-xs text-slate-500">SKU: {sku.sku_id}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-blue-500 rounded-full" 
                      style={{ width: `${Math.min(100, (sku.count / top_tried_skus[0].count) * 100)}%` }}
                    />
                  </div>
                  <span className="text-sm font-semibold text-blue-600 w-16 text-right">{sku.count} tries</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== Settings Panel ====================
const SettingsPanel = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    session_timeout_hours: 2,
    tryons_per_session: 5,
    auto_delete_photos_hours: 24,
    require_consent_for_camera: true
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await publicStudioAPI.getInstoreSettings();
        setSettings(response.data.settings);
        setFormData(response.data.settings);
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await publicStudioAPI.updateInstoreSettings(formData);
      setSettings(formData);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Session Settings */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-blue-600" />
          Session Settings
        </h3>
        <p className="text-sm text-slate-500 mb-4">
          Configure how customer sessions work in your store.
        </p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Session Duration (Hours)
            </label>
            <input
              type="number"
              min={1}
              max={24}
              value={formData.session_timeout_hours}
              onChange={(e) => setFormData({ ...formData, session_timeout_hours: parseInt(e.target.value) || 2 })}
              data-testid="settings-session-timeout"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-slate-400 mt-1">How long a customer&apos;s try-on session lasts (default: 2h)</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Try-ons Per Session
            </label>
            <input
              type="number"
              min={1}
              max={100}
              value={formData.tryons_per_session}
              onChange={(e) => setFormData({ ...formData, tryons_per_session: parseInt(e.target.value) || 5 })}
              data-testid="settings-tryons-per-session"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-slate-400 mt-1">Max try-ons per customer session (default: 5)</p>
          </div>
        </div>
      </div>

      {/* Rate Limits Info */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
        <div className="flex gap-3">
          <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-800">API Cost Management</p>
            <p className="text-xs text-blue-600 mt-1">
              Each customer gets a limited number of try-ons per session to help manage your API costs.
              You can give special customers extra try-ons from the Sessions tab.
            </p>
          </div>
        </div>
      </div>

      {/* Privacy Controls Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Lock className="w-5 h-5 text-blue-600" />
          Privacy Controls
        </h3>
        <p className="text-sm text-slate-500 mb-4">
          Manage how customer data is handled and protected.
        </p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Auto-Delete Photos After (Hours)
            </label>
            <input
              type="number"
              min={1}
              max={168}
              value={formData.auto_delete_photos_hours}
              onChange={(e) => setFormData({ ...formData, auto_delete_photos_hours: parseInt(e.target.value) || 24 })}
              className="w-full max-w-xs px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-slate-400 mt-1">Customer photos will be automatically deleted (1-168h / 7 days max)</p>
          </div>
          
          <div className="flex items-center justify-between py-3 border-t border-slate-100">
            <div>
              <p className="font-medium text-slate-800">Require Camera Consent</p>
              <p className="text-xs text-slate-500 mt-0.5">Ask customers for permission before using their camera</p>
            </div>
            <button
              onClick={() => setFormData({ ...formData, require_consent_for_camera: !formData.require_consent_for_camera })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.require_consent_for_camera ? 'bg-blue-600' : 'bg-slate-200'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                formData.require_consent_for_camera ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>
      </div>

      {/* Privacy Info Box */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
        <div className="flex gap-3">
          <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-800">Customer Privacy Protection</p>
            <p className="text-xs text-blue-600 mt-1">
              By default, you cannot view customer photos. If needed, you must request access and the customer must approve. 
              All photos are automatically deleted based on your settings above.
            </p>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 font-medium"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Settings
        </button>
      </div>
    </div>
  );
};

// ==================== Session Card Component ====================
const SessionCard = ({ session, onView }) => {
  const isCompleted = session.checkout_status === 'completed';
  const isActive = session.status === 'active' && !isCompleted;
  
  return (
    <div 
      className={`bg-white rounded-xl border p-4 hover:shadow-md transition-shadow cursor-pointer ${
        isCompleted ? 'border-emerald-200 bg-emerald-50/30' : 
        isActive ? 'border-blue-200' : 'border-slate-200'
      }`}
      onClick={() => onView(session)}
      data-testid={`session-card-${session.session_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isCompleted ? 'bg-emerald-100' : isActive ? 'bg-blue-100' : 'bg-slate-100'
          }`}>
            <User className={`w-5 h-5 ${
              isCompleted ? 'text-emerald-600' : isActive ? 'text-blue-600' : 'text-slate-500'
            }`} />
          </div>
          <div>
            {session.customer_name && (
              <p className="font-semibold text-slate-900">{session.customer_name}</p>
            )}
            <p className={`text-slate-600 ${session.customer_name ? 'text-sm' : 'font-medium'}`}>
              +91 {session.customer_phone_full || session.customer_phone}
            </p>
            <p className="text-xs text-slate-500">
              {new Date(session.created_at).toLocaleDateString('en-IN', {
                day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
              })}
            </p>
          </div>
        </div>
        
        <div className="text-right">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
            isCompleted ? 'bg-emerald-100 text-emerald-700' :
            isActive ? 'bg-blue-100 text-blue-700' : 
            'bg-slate-100 text-slate-600'
          }`}>
            {isCompleted ? (
              <><CheckCircle className="w-3 h-3" /> Checked Out</>
            ) : isActive ? (
              <><Clock className="w-3 h-3" /> Active</>
            ) : (
              'Expired'
            )}
          </span>
          {session.total_revenue > 0 && (
            <p className="text-sm font-semibold text-emerald-600 mt-1">
              ₹{session.total_revenue.toLocaleString()}
            </p>
          )}
        </div>
      </div>
      
      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100">
        <div className="flex items-center gap-1 text-sm text-slate-600">
          <Eye className="w-4 h-4 text-blue-500" />
          <span>{session.tryons_used} try-ons</span>
        </div>
        <div className="flex items-center gap-1 text-sm text-slate-600">
          <ShoppingBag className="w-4 h-4 text-blue-500" />
          <span>{session.soft_cart_count} tried</span>
        </div>
        <div className="flex items-center gap-1 text-sm text-slate-600">
          <ShoppingCart className="w-4 h-4 text-emerald-500" />
          <span>{session.hard_cart_count} in cart</span>
        </div>
      </div>
    </div>
  );
};

// ==================== Session Row Component (List View) ====================
const SessionRow = ({ session, onView }) => {
  const isCompleted = session.checkout_status === 'completed';
  const isActive = session.status === 'active' && !isCompleted;
  
  return (
    <tr 
      className={`hover:bg-slate-50 cursor-pointer transition-colors ${
        isCompleted ? 'bg-emerald-50/30' : isActive ? 'bg-blue-50/20' : ''
      }`}
      onClick={() => onView(session)}
      data-testid={`session-row-${session.session_id}`}
    >
      {/* Customer Info */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
            isCompleted ? 'bg-emerald-100' : isActive ? 'bg-blue-100' : 'bg-slate-100'
          }`}>
            <User className={`w-4 h-4 ${
              isCompleted ? 'text-emerald-600' : isActive ? 'text-blue-600' : 'text-slate-500'
            }`} />
          </div>
          <div className="min-w-0">
            {session.customer_name && (
              <p className="font-medium text-slate-900 text-sm truncate">{session.customer_name}</p>
            )}
            <p className={`text-slate-600 truncate ${session.customer_name ? 'text-xs' : 'text-sm font-medium'}`}>
              +91 {session.customer_phone_full || session.customer_phone}
            </p>
          </div>
        </div>
      </td>
      
      {/* Date/Time */}
      <td className="px-4 py-3 text-sm text-slate-600">
        {new Date(session.created_at).toLocaleDateString('en-IN', {
          day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
        })}
      </td>
      
      {/* Status */}
      <td className="px-4 py-3">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
          isCompleted ? 'bg-emerald-100 text-emerald-700' :
          isActive ? 'bg-blue-100 text-blue-700' : 
          'bg-slate-100 text-slate-600'
        }`}>
          {isCompleted ? (
            <><CheckCircle className="w-3 h-3" /> Checked Out</>
          ) : isActive ? (
            <><Clock className="w-3 h-3" /> Active</>
          ) : (
            'Expired'
          )}
        </span>
      </td>
      
      {/* Try-ons */}
      <td className="px-4 py-3 text-sm text-slate-600">
        <div className="flex items-center gap-1">
          <Eye className="w-3.5 h-3.5 text-blue-500" />
          {session.tryons_used}
        </div>
      </td>
      
      {/* Cart */}
      <td className="px-4 py-3 text-sm text-slate-600">
        <div className="flex items-center gap-1">
          <ShoppingCart className="w-3.5 h-3.5 text-emerald-500" />
          {session.hard_cart_count}
        </div>
      </td>
      
      {/* Revenue */}
      <td className="px-4 py-3 text-right">
        {session.total_revenue > 0 ? (
          <span className="text-sm font-semibold text-emerald-600">
            ₹{session.total_revenue.toLocaleString()}
          </span>
        ) : (
          <span className="text-sm text-slate-400">-</span>
        )}
      </td>
    </tr>
  );
};

// ==================== Session Detail Modal ====================
const SessionDetailModal = ({ session, onClose, onCheckoutComplete }) => {
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [checkoutItems, setCheckoutItems] = useState({});

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const response = await publicStudioAPI.getStaffSession(session.session_id);
        setDetails(response.data);
        
        // Initialize checkout items
        const items = {};
        response.data.hard_cart?.forEach(item => {
          items[item.product_id] = { action: 'sold', quantity: item.quantity };
        });
        setCheckoutItems(items);
      } catch (error) {
        toast.error('Failed to load session details');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDetails();
  }, [session.session_id]);

  const handleCheckout = async () => {
    if (!details?.hard_cart?.length) {
      toast.error('No items in cart to checkout');
      return;
    }

    setProcessing(true);
    try {
      const items = Object.entries(checkoutItems).map(([product_id, data]) => ({
        product_id,
        action: data.action,
        quantity: data.quantity,
        reason: data.reason
      }));

      const response = await publicStudioAPI.processCheckout(session.session_id, { items });
      toast.success(`Checkout complete! ${response.data.sold_count} items sold, ₹${response.data.total_revenue.toLocaleString()} revenue`);
      onCheckoutComplete();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Checkout failed');
    } finally {
      setProcessing(false);
    }
  };

  const isCheckedOut = details?.checkout_status === 'completed';

  // Calculate cart total
  const cartTotal = details?.hard_cart?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Customer Info */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                <User className="w-7 h-7" />
              </div>
              <div>
                {details?.customer_name && (
                  <h2 className="text-xl font-bold">{details.customer_name}</h2>
                )}
                <p className="text-white/90 text-lg">+91 {details?.customer_phone || session.customer_phone}</p>
                <div className="flex items-center gap-3 mt-1 text-sm text-white/70">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    {details?.created_at ? new Date(details.created_at).toLocaleDateString('en-IN', {
                      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
                    }) : '-'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {details?.created_at ? new Date(details.created_at).toLocaleTimeString('en-IN', {
                      hour: '2-digit', minute: '2-digit'
                    }) : '-'}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                isCheckedOut ? 'bg-emerald-500' : 'bg-white/20'
              }`}>
                {isCheckedOut ? '✓ Checked Out' : 'Active Session'}
              </span>
              <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg ml-2">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-blue-50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-blue-700">{details?.tryons_used || 0}</p>
                  <p className="text-xs text-blue-600 mt-1">Try-ons Done</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-blue-700">{details?.soft_cart?.length || 0}</p>
                  <p className="text-xs text-blue-600 mt-1">Items Tried</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-700">{details?.hard_cart?.length || 0}</p>
                  <p className="text-xs text-emerald-600 mt-1">In Cart</p>
                </div>
              </div>

              {/* Items to Purchase (Hard Cart) - Show this prominently */}
              <div className="bg-emerald-50 rounded-xl p-4 border-2 border-emerald-200">
                <h3 className="font-bold text-emerald-800 mb-3 flex items-center gap-2 text-lg">
                  <ShoppingCart className="w-5 h-5" />
                  Ready to Purchase ({details?.hard_cart?.length || 0})
                </h3>
                {details?.hard_cart?.length > 0 ? (
                  <div className="space-y-3">
                    {details.hard_cart.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
                        <div className="w-16 h-20 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                          {item.image_url ? (
                            <img src={item.image_url} alt={item.product_name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="w-6 h-6 text-slate-400" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-800">{item.product_name}</p>
                          <p className="text-xs text-slate-500">SKU: {item.sku_id}</p>
                          <div className="flex items-center gap-3 mt-2">
                            <span className="text-lg font-bold text-emerald-700">₹{item.price?.toLocaleString()}</span>
                            <span className="text-sm text-slate-600">× {item.quantity}</span>
                            {item.current_stock !== undefined && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                item.current_stock > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {item.current_stock > 0 ? `${item.current_stock} in stock` : 'Out of stock'}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-slate-900">
                            ₹{(item.price * item.quantity).toLocaleString()}
                          </p>
                          {/* Checkout Action */}
                          {!isCheckedOut && (
                            <select
                              value={checkoutItems[item.product_id]?.action || 'sold'}
                              onChange={(e) => setCheckoutItems(prev => ({
                                ...prev,
                                [item.product_id]: { ...prev[item.product_id], action: e.target.value }
                              }))}
                              className="mt-1 text-xs px-2 py-1 border border-slate-200 rounded-lg bg-white"
                            >
                              <option value="sold">✓ Sold</option>
                              <option value="not_purchased">✗ Not Purchased</option>
                            </select>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    {/* Cart Total */}
                    <div className="flex items-center justify-between pt-3 mt-3 border-t-2 border-emerald-300">
                      <span className="font-bold text-emerald-800 text-lg">TOTAL</span>
                      <span className="text-2xl font-bold text-emerald-700">
                        ₹{cartTotal.toLocaleString()}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-emerald-700 text-center py-4">No items added to cart yet</p>
                )}
              </div>

              {/* Items Tried (Soft Cart) - Collapsible */}
              <details className="group">
                <summary className="cursor-pointer list-none">
                  <div className="flex items-center justify-between bg-slate-50 rounded-xl p-3 hover:bg-slate-100 transition-colors">
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                      <ShoppingBag className="w-4 h-4 text-blue-600" />
                      Items Tried ({details?.soft_cart?.length || 0})
                    </h3>
                    <ChevronDown className="w-5 h-5 text-slate-500 group-open:rotate-180 transition-transform" />
                  </div>
                </summary>
                <div className="mt-3">
                  {details?.soft_cart?.length > 0 ? (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {details.soft_cart.map((item, idx) => (
                        <div key={idx} className="bg-slate-50 rounded-lg p-2">
                          <div className="aspect-[3/4] rounded-lg bg-slate-200 mb-2 overflow-hidden">
                            {item.image_url ? (
                              <img src={item.image_url} alt={item.product_name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package className="w-8 h-8 text-slate-400" />
                              </div>
                            )}
                          </div>
                          <p className="text-xs font-medium text-slate-800 truncate">{item.product_name}</p>
                          <p className="text-xs text-slate-500">₹{item.price?.toLocaleString()}</p>
                          <p className="text-xs text-blue-600">Tried {item.tryon_count}x</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-4">No items tried yet</p>
                  )}
                </div>
              </details>

              {/* Checkout Info (if completed) */}
              {isCheckedOut && details?.checkout_data && (
                <div className="bg-emerald-100 rounded-xl p-4 border border-emerald-200">
                  <h3 className="font-bold text-emerald-800 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Checkout Summary
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-emerald-700">Items Sold</p>
                      <p className="text-xl font-bold text-emerald-800">{details.checkout_data.sold_items?.length || 0}</p>
                    </div>
                    <div>
                      <p className="text-sm text-emerald-700">Total Revenue</p>
                      <p className="text-xl font-bold text-emerald-800">₹{details.checkout_data.total_revenue?.toLocaleString()}</p>
                    </div>
                  </div>
                  <p className="text-xs text-emerald-600 mt-3">
                    Checkout completed at {new Date(details.checkout_completed_at).toLocaleString('en-IN')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer - Checkout Button */}
        {!isCheckedOut && details?.hard_cart?.length > 0 && (
          <div className="p-4 border-t border-slate-200 bg-gradient-to-r from-emerald-50 to-green-50">
            <div className="flex items-center justify-between mb-3">
              <span className="text-slate-600">Total Bill Amount</span>
              <span className="text-2xl font-bold text-emerald-700">₹{cartTotal.toLocaleString()}</span>
            </div>
            <button
              onClick={handleCheckout}
              disabled={processing}
              data-testid="complete-checkout-btn"
              className="w-full py-3.5 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl font-bold text-lg hover:from-emerald-700 hover:to-green-700 disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg"
            >
              {processing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Complete Checkout & Create Bill
                </>
              )}
            </button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

// ==================== Main Component ====================
const InStoreTryOnTab = ({ displayMode = 'card' }) => {
  // Sub-tab state
  const [activeSubTab, setActiveSubTab] = useState('sessions'); // 'sessions' | 'analytics' | 'settings'
  const [analyticsPeriod, setAnalyticsPeriod] = useState('7d');
  
  // Session management state
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('phone'); // 'phone' | 'name'
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch all sessions
  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await publicStudioAPI.getAllSessions({
        status: statusFilter || undefined,
        page,
        limit: 20
      });
      setSessions(response.data.sessions || []);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page]);

  useEffect(() => {
    if (activeSubTab === 'sessions') {
      fetchSessions();
    }
  }, [fetchSessions, activeSubTab]);

  // Search customer by phone or name
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error('Enter a search term');
      return;
    }

    if (searchType === 'phone' && searchQuery.length < 10) {
      toast.error('Enter a valid 10-digit phone number');
      return;
    }

    setSearching(true);
    try {
      const params = searchType === 'phone' 
        ? { phone: searchQuery } 
        : { name: searchQuery };
      const response = await publicStudioAPI.searchCustomer(params);
      setSearchResults(response.data);
      if (response.data.sessions.length === 0) {
        toast(`No sessions found for "${searchQuery}"`, { icon: '📭' });
      }
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  const displaySessions = searchResults ? searchResults.sessions : sessions;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-50 via-white to-blue-50 rounded-xl p-6 border border-blue-100">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-100 rounded-xl">
            <Store className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">In-Store TryOn Studio</h2>
            <p className="text-slate-600 text-sm mt-1">
              Manage customer sessions, view analytics, and configure settings for your in-store try-on experience.
            </p>
          </div>
        </div>
      </div>

      {/* Sub-Tab Navigation */}
      <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveSubTab('sessions')}
          data-testid="instore-subtab-sessions"
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
            activeSubTab === 'sessions'
              ? 'bg-white text-blue-700 shadow-sm'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <Users className="w-4 h-4" />
          Sessions
        </button>
        <button
          onClick={() => setActiveSubTab('analytics')}
          data-testid="instore-subtab-analytics"
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
            activeSubTab === 'analytics'
              ? 'bg-white text-blue-700 shadow-sm'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <BarChart3 className="w-4 h-4" />
          Analytics
        </button>
        <button
          onClick={() => setActiveSubTab('settings')}
          data-testid="instore-subtab-settings"
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
            activeSubTab === 'settings'
              ? 'bg-white text-blue-700 shadow-sm'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <Settings className="w-4 h-4" />
          Settings
        </button>
      </div>

      {/* Sessions Sub-Tab Content */}
      {activeSubTab === 'sessions' && (
        <>
          {/* Search Bar */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search Type Toggle */}
              <div className="flex items-center bg-slate-100 p-1 rounded-lg">
                <button
                  onClick={() => { setSearchType('phone'); setSearchQuery(''); }}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${
                    searchType === 'phone' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600'
                  }`}
                >
                  <Phone className="w-3.5 h-3.5" /> Phone
                </button>
                <button
                  onClick={() => { setSearchType('name'); setSearchQuery(''); }}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${
                    searchType === 'name' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600'
                  }`}
                >
                  <User className="w-3.5 h-3.5" /> Name
                </button>
              </div>
              
              {/* Search Input */}
              <div className="flex-1 relative">
                {searchType === 'phone' ? (
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                ) : (
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                )}
                <input
                  type={searchType === 'phone' ? 'tel' : 'text'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(
                    searchType === 'phone' 
                      ? e.target.value.replace(/\D/g, '').slice(0, 10) 
                      : e.target.value
                  )}
                  placeholder={searchType === 'phone' ? 'Enter 10-digit phone number...' : 'Search by customer name...'}
                  data-testid="instore-search-input"
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
              
              {/* Search Button */}
              <button
                onClick={handleSearch}
                disabled={searching || !searchQuery.trim() || (searchType === 'phone' && searchQuery.length < 10)}
                data-testid="instore-search-btn"
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Search
              </button>
              
              {/* Clear Button */}
              {searchResults && (
                <button
                  onClick={clearSearch}
                  className="px-4 py-2.5 border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Clear
                </button>
              )}
            </div>
            
            {/* Search Results Info */}
            {searchResults && (
              <p className="text-sm text-slate-600 mt-3">
                Found <strong>{searchResults.total}</strong> session(s) 
                {searchResults.search_type === 'phone' 
                  ? ` for phone +91 ${searchResults.query}` 
                  : ` matching name "${searchResults.query}"`}
              </p>
            )}
          </div>

          {/* Filters (only show when not searching) */}
          {!searchResults && (
            <div className="flex items-center gap-3">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                data-testid="instore-status-filter"
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
              >
                <option value="">All Sessions</option>
                <option value="active">Active</option>
                <option value="completed">Checked Out</option>
              </select>
              <button
                onClick={fetchSessions}
                data-testid="instore-refresh-btn"
                className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Sessions List */}
          <div className="space-y-3">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : displaySessions.length === 0 ? (
              <div className="text-center py-12 bg-slate-50 rounded-xl">
                <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No sessions found</p>
                <p className="text-sm text-slate-400 mt-1">Sessions will appear when customers use your in-store QR code</p>
              </div>
            ) : displayMode === 'list' ? (
              <>
                {/* List/Table View */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="instore-sessions-table">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Customer</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Date/Time</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Try-ons</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Cart</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Revenue</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {displaySessions.map((session) => (
                          <SessionRow
                            key={session.session_id}
                            session={session}
                            onView={setSelectedSession}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination (only for all sessions view) */}
                {!searchResults && totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 pt-4">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-3 py-1.5 border border-slate-200 rounded-lg disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-slate-600">
                      Page {page} of {totalPages}
                    </span>
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="px-3 py-1.5 border border-slate-200 rounded-lg disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Card View (default) */}
                <div className="grid gap-3 sm:grid-cols-2" data-testid="instore-sessions-list">
                  {displaySessions.map((session) => (
                    <SessionCard
                      key={session.session_id}
                      session={session}
                      onView={setSelectedSession}
                    />
                  ))}
                </div>

                {/* Pagination (only for all sessions view) */}
                {!searchResults && totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 pt-4">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-3 py-1.5 border border-slate-200 rounded-lg disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-slate-600">
                      Page {page} of {totalPages}
                    </span>
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="px-3 py-1.5 border border-slate-200 rounded-lg disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Analytics Sub-Tab Content */}
      {activeSubTab === 'analytics' && (
        <AnalyticsPanel 
          period={analyticsPeriod} 
          onPeriodChange={setAnalyticsPeriod} 
        />
      )}

      {/* Settings Sub-Tab Content */}
      {activeSubTab === 'settings' && (
        <SettingsPanel />
      )}

      {/* Session Detail Modal */}
      <AnimatePresence>
        {selectedSession && (
          <SessionDetailModal
            session={selectedSession}
            onClose={() => setSelectedSession(null)}
            onCheckoutComplete={() => {
              fetchSessions();
              if (searchResults) {
                handleSearch();
              }
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default InStoreTryOnTab;
